import { Injectable } from '@angular/core';
import {AuthService} from '../auth.service';
import {SchoolModel} from '../../models/school/school.model';
import {Observable} from 'rxjs';
import {
  SchoolActivationModel,
  SchoolActivityModel,
  SchoolSkillsAnalyticsModel
} from '../../models/school/school-analytics.model';
import {map} from 'rxjs/operators';

/** Query params for school analytics endpoints (activation criteria + optional date range). */
export interface SchoolAnalyticsQueryParams {
  grade?: number | null;
  activationMode?: 'answers' | 'placement';
  minAnswers?: number;
  from?: string;
  to?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SchoolAdminAnalyticsService {

  constructor(private authSv: AuthService) { }

  private buildSchoolAnalyticsQuery(params?: SchoolAnalyticsQueryParams): string {

    if (!params) {
      return '';
    }

    const parts: string[] = [];
    if (params.grade != null) {
      parts.push(`grade=${encodeURIComponent(String(params.grade))}`);
    }
    if (params.activationMode) {
      parts.push(`activationMode=${encodeURIComponent(params.activationMode)}`);
    }
    if (params.minAnswers != null) {
      parts.push(`minAnswers=${encodeURIComponent(String(params.minAnswers))}`);
    }
    if (params.from) {
      parts.push(`from=${encodeURIComponent(params.from)}`);
    }
    if (params.to) {
      parts.push(`to=${encodeURIComponent(params.to)}`);
    }

    return parts.length ? `?${parts.join('&')}` : '';

  }

  fetchOverview(
    schoolId: string = SchoolModel.getCurrent()._id,
    params?: SchoolAnalyticsQueryParams
  ): Observable<SchoolActivityModel> {

    const query = this.buildSchoolAnalyticsQuery(params);
    return this.authSv.get(`/analytics/${schoolId}/overview${query}`).pipe(map((response) => {

      const activityOverview = SchoolActivityModel.generate(response.data);
      return activityOverview;

    }));

  }

  fetchActivations(
    schoolId: string = SchoolModel.getCurrent()._id,
    params?: SchoolAnalyticsQueryParams
  ): Observable<SchoolActivationModel> {

    const query = this.buildSchoolAnalyticsQuery(params);
    return this.authSv.get(`/analytics/${schoolId}/activation${query}`).pipe(map((response) => {

      const activations = SchoolActivationModel.generate(response.data);
      return activations;

    }));

  }

  /**
   * GET /analytics/:schoolId/skills
   * Without grade: summary per grade (mastery vs baseline chart).
   * With grade: category + skill group breakdown for that grade.
   */
  fetchSkills(
    schoolId: string = SchoolModel.getCurrent()._id,
    params?: SchoolAnalyticsQueryParams
  ): Observable<SchoolSkillsAnalyticsModel> {

    const query = this.buildSchoolAnalyticsQuery(params);
    return this.authSv.get(`/analytics/${schoolId}/skills${query}`).pipe(map((response) => {

      return SchoolSkillsAnalyticsModel.generate(response.data);

    }));

  }

}
